import FeatureGates from '@atlaskit/feature-gate-js-client';

import { createRelayResource } from '@townsquare/relay-utils';
import { HomeResourceContext } from '@townsquare/router';
import { TEAMS_AS_A_STANDALONE_APP } from '@townsquare/stat-sig/experiments';
import { isGateEnabled } from '@townsquare/stat-sig/gate';

import { getPeopleFieldType } from '../components/ScreenType';
import StaffDirectoryViewQuery, {
  StaffDirectoryViewQuery as StaffDirectoryViewQueryType,
} from '../components/__generated__/StaffDirectoryViewQuery.graphql';
import { getScreenFromStringOrDefault } from '../config/ScreenConfig';

import { appendEmptyTeamSearchToTql, updateTeamQueryBasedOnScreen } from './utils';

export const staffDirectoryResource = createRelayResource<StaffDirectoryViewQueryType>({
  getQuery: (routerContext, resourceContext) => {
    const context = resourceContext as HomeResourceContext;
    const user = context.user;

    const screen = getScreenFromStringOrDefault(routerContext.query.screen);

    const isSearchAllScreen = screen == 'SEARCH_ALL';
    const isSearchPeopleScreen = screen == 'SEARCH_PEOPLE';
    const isAllTeamsScreen = screen === 'SEARCH_TEAMS';
    const isYourTeamsScreen = screen === 'YOUR_TEAMS';
    const isSearchTeamsScreen = isAllTeamsScreen || isYourTeamsScreen;
    const isBrowseAllScreen = screen == 'BROWSE_ALL';
    const isBrowsePeopleScreen =
      screen == 'BROWSE_DEPARTMENTS' || screen == 'BROWSE_LOCATIONS' || screen == 'BROWSE_JOB_TITLES';
    const isSearchKudosScreen = screen == 'BROWSE_KUDOS';

    // We define a cachekey so that it only updates when the screen changes, not tql etc (those updates are handled by the components)
    const cacheKey = `relay-staff-directory-${screen.toString()}`;

    const teamQuery =
      FeatureGates.initializeCompleted() && FeatureGates.getExperimentValue(TEAMS_AS_A_STANDALONE_APP, 'enabled', false)
        ? updateTeamQueryBasedOnScreen(routerContext.query.tql ?? '', screen, user)
        : routerContext.query.tql ?? (isBrowseAllScreen ? `member = ${user.accountId}` : '');
    const userQuery = routerContext.query.tql ?? '';
    const kudosQuery = routerContext.query.tql ?? '';
    const organisationId = context.workspace?.organisationId ?? context.orgId ?? '';
    const cloudId =
      ((isGateEnabled('townsquare_teams_app_allow_no_workspace_id')
        ? context.collabContext?.rovoWorkspace
        : undefined) ||
        context.workspace?.cloudId) ??
      '';
    const useCloudId = isGateEnabled('townsquare_teams_app_allow_no_workspace_id');

    // we get fewer results for the search all screen as it displays results from people and from teams. (We also fetch more than we intend to display as CPUS has unreliable counting)
    const numResultsToFetch = isSearchAllScreen ? 12 : 20;

    const workspaceId = context.workspace?.globalId ?? '';
    const kudosEnabled = Boolean(workspaceId);

    return {
      query: StaffDirectoryViewQuery,
      variables: {
        teamQuery: appendEmptyTeamSearchToTql(teamQuery),
        userQuery,
        kudosQuery,
        workspaceId,
        organisationId,
        cloudId,
        first: numResultsToFetch,
        peopleFieldType: getPeopleFieldType(screen),
        isSearchAllScreen,
        isSearchPeopleScreen,
        isSearchTeamsScreen,
        isBrowseAllScreen,
        isBrowsePeopleScreen,
        isSearchKudosScreen,
        isYourTeamsScreen,
        kudosEnabled,
        useCloudId,
      },
      cacheKey: cacheKey,
      options: { fetchPolicy: 'store-and-network' },
    };
  },
});
